import {
  GAS_LIMIT_EXCEEDED_ERROR_CODE,
  GAS_LIMIT_EXCEEDED_USER_MESSAGE,
  GUARDIAN_TRANSACTION_STATUS_MESSAGES,
  isGuardianTransactionFinished,
  type GuardianTransactionStatus,
} from '@mls-wallet/shared-types';
import type { ExecutionTarget, ProposalExecutionType } from './execution-state';

export const PARTIAL_EXECUTION_USER_MESSAGE =
  GUARDIAN_TRANSACTION_STATUS_MESSAGES.PARTIALLY_EXECUTED;
export const SUCCESS_AUTO_DISMISS_MS = 3_000;

/**
 * Returns the customer-facing noun used in tray titles.
 *
 * Transactions have a fixed noun, while proposals include their singular
 * domain type when one is known.
 */
export function getExecutionSubject(
  executionTarget: ExecutionTarget,
  proposalType: ProposalExecutionType | null
): string {
  return executionTarget === 'transaction'
    ? 'transaction'
    : `${proposalType ?? ''} proposal`.trim();
}

/**
 * Reports whether the card may expose its dismiss action.
 *
 * Local request failures and terminal polling failures are dismissible even
 * though they do not have a finished GuardianTransaction status.
 */
export function isExecutionDismissible(
  status: GuardianTransactionStatus | undefined,
  hasImmediateError: boolean,
  hasPollingError: boolean
): boolean {
  return (
    hasImmediateError ||
    hasPollingError ||
    (!!status && isGuardianTransactionFinished(status))
  );
}

/**
 * Returns the success-only dismissal delay.
 *
 * Failure and support-required states intentionally remain visible until the
 * user acknowledges them.
 */
export function getExecutionAutoDismissDelay(
  status: GuardianTransactionStatus | undefined,
  hasImmediateError: boolean,
  hasPollingError: boolean
): number | null {
  return status === 'COMPLETED' && !hasImmediateError && !hasPollingError
    ? SUCCESS_AUTO_DISMISS_MS
    : null;
}

/**
 * Maps GuardianTransaction progress and failures to execution-tray copy.
 *
 * Backend explanations win for relevant terminal states, while special states
 * such as validation, gas-limit blocking, and partial execution use curated
 * customer-facing guidance.
 */
export function getExecutionStatusMessage(
  status: GuardianTransactionStatus | undefined,
  errorMessage: string | null | undefined,
  executionTarget: ExecutionTarget
): string {
  // Case: the request exists locally but has not returned a GuardianTransaction status.
  if (!status) {
    return 'Initializing...';
  }

  // Case: execution failed or reverted and may include a backend explanation.
  if (status === 'FAILED' || status === 'REVERTED') {
    return errorMessage || GUARDIAN_TRANSACTION_STATUS_MESSAGES[status];
  }

  // Case: some networks executed and support may be required for remediation.
  if (status === 'PARTIALLY_EXECUTED') {
    return PARTIAL_EXECUTION_USER_MESSAGE;
  }

  // Case: the transaction exceeded the configured gas safety limit.
  if (status === 'BLOCKED' && errorMessage === GAS_LIMIT_EXCEEDED_ERROR_CODE) {
    return GAS_LIMIT_EXCEEDED_USER_MESSAGE;
  }

  // Case: another backend guard blocked execution.
  if (status === 'BLOCKED') {
    return errorMessage || GUARDIAN_TRANSACTION_STATUS_MESSAGES[status];
  }

  // Case: validation copy differs between transactions and proposals.
  if (status === 'VALIDATING') {
    return executionTarget === 'transaction'
      ? 'Validating transaction...'
      : 'Validating proposal...';
  }

  return GUARDIAN_TRANSACTION_STATUS_MESSAGES[status];
}
